/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { SourceType } from 'client-code/channels-service';

import {
    ACTION_STRIP_HIGHLIGHT_LEFT_OFFSET,
    CHANNEL_NUMBER_MAX_LENGTH,
    ENTER_CHANNEL_NUMBER_DISPLAY_TIME,
    FULL_INFO_BANNER_DISPLAY_TIME,
    STANDARD_INFO_BANNER_DISPLAY_TIME,
    TRICK_PLAY_DISPLAY_TIME,
} from '../../../config';
import { DimensionScaler } from '../../../core/device/DimensionScaler';
import { ServicesClientImpl } from '../../../core/interactors/ServicesClientImpl';
import { QoEAppLogger } from '../../../core/logging';
import { Log } from '../../../core/logging/Log';
import { VuLogger } from '../../../core/logging/VULogger';
import { KeyManager } from '../../../core/navigation';
import { Keys } from '../../../core/navigation/Keys';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { PlaybackContentType } from '../../../core/playback/enums';
import { PlaybackDataModelImpl } from '../../../core/playback/impls';
import type { TivoPayloadAction } from '../../../core/tivo/tivo-redux-wrapper';
import { CcbSwitch } from '../../../core/utils/ccb';
import { CcbSwitchName } from '../../../core/utils/ccb/enums';
import {
    MetadataType,
    PlaybackRestrictionType,
    PlayerViewMode,
} from '../../../core/videoplayer/enums';
import { IPlayable } from '../../../core/videoplayer/interfaces';
import { VideoPlaybackController } from '../../../core/videoplayer/VideoPlaybackController';
import { launchVodScreen } from '../../jumpchannel/jumpchannel.reducers';
import { setScreenTaskController } from '../../screens/screens.reducers';
import {
    hideNotSupportedText,
    resetPlaybackParamsVideoplayer,
    showPCPinOverlayForVideoPlayer,
} from '../../videoplayer/videoplayer.reducers';
import {
    infoBannerSetBannerTipVisibilityAction,
    setActionItemsCountAction,
    setActionsStripHighlightBorderActiveAction,
    setActionsStripHighlightIndexAction,
    setAvailableActionItemsAction,
    setCurrentActiveIndexAction,
    setEnteredChannelNumberAction,
    setEnteringChannelNumberEnabledAction,
    setIsActionsStripActive,
    setIsFullInfoActive,
    setIsStandardInfoActive,
    setIsTrickplayMenuActive,
    setMediaButtonMouseOverIndex,
    updateEnteringChannelNumberTimeoutIdAction,
    updateFullInfoTimeoutIdAction,
    updateStandardInfoTimeoutIdAction,
    updateTrickPlayBarLongKeyPressIntervalIdAction,
    updateTrickPlayBarTimeoutIdAction,
} from './infobanner.actions';
import {
    INFO_BANNER_SET_BANNER_TIP_VISIBLITY,
    SET_ACTION_BUTTON_ACTIVE_POSITIONS_AVAILABLE,
    SET_ACTION_BUTTON_CURRENT_ACTIVE_INDEX,
    SET_ACTIONS_STRIP_ACTION_ITEMS_COUNT,
    SET_ACTIONS_STRIP_AVAILABLE_ACTION_ITEM,
    SET_ACTIONS_STRIP_HIGHLIGHT_BORDER_ACTIVE,
    SET_ACTIONS_STRIP_HIGHLIGHT_INDEX,
    SET_ENTERED_CHANNEL_NUMBER,
    SET_ENTERTING_CHANNEL_NUMBER_ENABLED,
    SET_IS_ACTIONS_STRIP_ACTIVE,
    SET_IS_FULL_INFO_ACTIVE,
    SET_IS_STANDARD_INFO_ACTIVE,
    SET_IS_TRICKPLAY_MENU_ACTIVE,
    SET_MEDIA_BUTTON_MOUSE_OVER_INDEX,
    UPDATE_ACTION_STRIP_REFERECE,
    UPDATE_ENTERING_CHANNEL_NUMBER_ENABLED_TIMER,
    UPDATE_FULL_INFO_TIMER,
    UPDATE_STANDARD_INFO_TIMER,
    UPDATE_TRICKPLAY_BAR_LONG_KEY_PRESS_INTERVAL_ID,
    UPDATE_TRICKPLAY_TIMER,
} from './infobanner.types';
import { MediaButtonState, MediaButtonTypes } from './mediabuttontypes';
import { VodInteractor } from '../../../core/interactors';
import { isJumpChannelCcbSwitchEnabled } from '../../../core/utils/ccb/CcbSwitchHelper';

const initialState: {
    actionsStripHighlightIndex: number;
    actionsStripHighlightBorderActive: boolean;
    actionItemsCount: number;
    isActionsStripActive: boolean;
    isBannerTipVisible: boolean;
    isStandardInfoActive: boolean;
    isFullInfoActive: boolean;
    isTrickplayMenuActive: boolean;
    activePositionsAvailable: Map<MediaButtonTypes, MediaButtonState>;
    currentActiveIndex: number;
    availableActionItems: Array<any>;
    standardInfoTimeoutId: any;
    fullInfoTimeoutId: any;
    trickPlayBarTimeoutId: any;
    actionStripReference: React.RefObject<HTMLDivElement> | null;
    enteringChannelNumberEnabled: boolean;
    channelNumberEntered: string;
    enteringChannelNumberTimeoutId: any;
    trickPlayBarLongKeyPressIntervalId: any;
    mediaButtonMouseOverIndex: number;
} = {
    actionsStripHighlightIndex: 0,
    actionsStripHighlightBorderActive: false,
    actionItemsCount: 0,
    isActionsStripActive: false,
    isBannerTipVisible: false,
    isStandardInfoActive: false,
    isFullInfoActive: false,
    isTrickplayMenuActive: false,
    activePositionsAvailable: new Map([]),
    currentActiveIndex: MediaButtonTypes.PLAY_PAUSE_BUTTON_INDEX,
    availableActionItems: [],
    standardInfoTimeoutId: null,
    fullInfoTimeoutId: null,
    trickPlayBarTimeoutId: null,
    actionStripReference: null,
    enteringChannelNumberEnabled: false,
    channelNumberEntered: '',
    enteringChannelNumberTimeoutId: null,
    trickPlayBarLongKeyPressIntervalId: null,
    mediaButtonMouseOverIndex: -1,
};

const TAG: string = 'INFOBANNER';

const infoBannerReducers = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<typeof initialState>>{},
) => {
    switch (type) {
        case INFO_BANNER_SET_BANNER_TIP_VISIBLITY:
            return {
                ...state,
                isBannerTipVisible: payload.isBannerTipVisible,
            };
        case SET_ACTIONS_STRIP_HIGHLIGHT_INDEX:
            return {
                ...state,
                actionsStripHighlightIndex: payload.actionsStripHighlightIndex,
            };

        case SET_ACTIONS_STRIP_HIGHLIGHT_BORDER_ACTIVE:
            return {
                ...state,
                actionsStripHighlightBorderActive: payload.actionsStripHighlightBorderActive,
            };

        case SET_ACTIONS_STRIP_AVAILABLE_ACTION_ITEM:
            return {
                ...state,
                availableActionItems: payload.availableActionItems,
            };
        case SET_ACTIONS_STRIP_ACTION_ITEMS_COUNT:
            return {
                ...state,
                actionItemsCount: payload.actionItemsCount,
            };
        case SET_IS_ACTIONS_STRIP_ACTIVE:
            return {
                ...state,
                isActionsStripActive: payload.isActionsStripActive,
            };
        case SET_IS_FULL_INFO_ACTIVE:
            return {
                ...state,
                isFullInfoActive: payload.isFullInfoActive,
            };
        case SET_IS_STANDARD_INFO_ACTIVE:
            return {
                ...state,
                isStandardInfoActive: payload.isStandardInfoActive,
            };
        case SET_IS_TRICKPLAY_MENU_ACTIVE:
            return {
                ...state,
                isTrickplayMenuActive: payload.isTrickplayMenuActive,
            };
        case SET_ACTION_BUTTON_ACTIVE_POSITIONS_AVAILABLE:
            return {
                ...state,
                activePositionsAvailable: payload.activePositionsAvailable,
            };
        case SET_ACTION_BUTTON_CURRENT_ACTIVE_INDEX:
            return {
                ...state,
                currentActiveIndex: payload.currentActiveIndex,
            };
        case UPDATE_STANDARD_INFO_TIMER:
            return {
                ...state,
                standardInfoTimeoutId: payload.standardInfoTimeoutId,
            };
        case UPDATE_FULL_INFO_TIMER:
            return {
                ...state,
                fullInfoTimeoutId: payload.fullInfoTimeoutId,
            };
        case UPDATE_TRICKPLAY_TIMER:
            return {
                ...state,
                trickPlayBarTimeoutId: payload.trickPlayBarTimeoutId,
            };
        case UPDATE_ACTION_STRIP_REFERECE:
            return {
                ...state,
                actionStripReference: payload.actionStripReference,
            };
        case SET_ENTERTING_CHANNEL_NUMBER_ENABLED:
            return {
                ...state,
                enteringChannelNumberEnabled: payload.enteringChannelNumberEnabled,
            };
        case SET_ENTERED_CHANNEL_NUMBER:
            return {
                ...state,
                channelNumberEntered: payload.channelNumberEntered,
            };
        case UPDATE_ENTERING_CHANNEL_NUMBER_ENABLED_TIMER:
            return {
                ...state,
                enteringChannelNumberTimeoutId: payload.enteringChannelNumberTimeoutId,
            };
        case UPDATE_TRICKPLAY_BAR_LONG_KEY_PRESS_INTERVAL_ID:
            return {
                ...state,
                trickPlayBarLongKeyPressIntervalId: payload.trickPlayBarLongKeyPressIntervalId,
            };
        case SET_MEDIA_BUTTON_MOUSE_OVER_INDEX:
            return {
                ...state,
                mediaButtonMouseOverIndex: payload.mediaButtonMouseOverIndex,
            };
        default:
            return state;
    }
};

export const deleteLastEnteredChannelDigit = () => (dispatch: Function, getState: Function) => {
    const { infoBanner } = getState();
    const { channelNumberEntered } = infoBanner;
    dispatch(setEnteredChannelNumberAction(channelNumberEntered.slice(0, -1)));
    dispatch(updateEnteringChannelnumberEnabledTimer());
};

export const infoBannerHideBannerTip = () => (dispatch: Function) => {
    dispatch(infoBannerSetBannerTipVisibilityAction(false));
};

export const infoBannerShowBannerTip = () => (dispatch: Function) => {
    dispatch(infoBannerSetBannerTipVisibilityAction(true));
};

export const updateEnteringChannelnumberEnabledTimer =
    () => (dispatch: Function, getState: Function) => {
        const { infoBanner } = getState();
        const { enteringChannelNumberTimeoutId, enteringChannelNumberEnabled } = infoBanner;
        if (enteringChannelNumberTimeoutId) clearTimeout(enteringChannelNumberTimeoutId);
        const timerId = setTimeout(() => {
            if (enteringChannelNumberEnabled) {
                dispatch(tuneToEnteredChannelNumber());
                dispatch(setEnteringChannelNumberEnabled(false));
                dispatch(setEnteredChannelNumberAction(''));
            }
        }, ENTER_CHANNEL_NUMBER_DISPLAY_TIME);
        dispatch(updateEnteringChannelNumberTimeoutIdAction(timerId));
        Diagnostics.info(TAG, 'updateEnteringChannelnumberEnabledTimer');
        Log.i(TAG, 'updateEnteringChannelnumberEnabledTimer');
    };

export const tuneToEnteredChannelNumber = () => (dispatch: Function, getState: Function) => {
    const { infoBanner } = getState();
    const { channelNumberEntered } = infoBanner;
    const services = new VodInteractor();
    const getChannelFromChannelNumber = isJumpChannelCcbSwitchEnabled()
        ? services.getChannelFromChannelNumberV2
        : services.getChannelFromChannelNumber;
    const playerInstance = VideoPlaybackController.getInstance();
    getChannelFromChannelNumber(channelNumberEntered)
        .then((data) => {
            // TODO: Remove this check on completion of ENABLE_VOD_JUMP_CHANNEL_REMOVE_IN_KST_12189
            const isJumpChannelEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
                CcbSwitchName.ENABLE_VOD_JUMP_CHANNEL_REMOVE_IN_KST_12189,
            );
            if (
                isJumpChannelEnabled &&
                data.sourceType === SourceType.JumpChannel &&
                data.channelNumber
            ) {
                dispatch(launchVodScreen(data.channelNumber));
                Log.i(
                    TAG,
                    'tuneToEnteredChannelNumber',
                    'Launched VOD screen for channel:',
                    data.channelNumber,
                );
                return;
            }
            const oVideoPlaybackController = VideoPlaybackController.getInstance();
            dispatch(hideNotSupportedText());
            dispatch(resetPlaybackParamsVideoplayer());
            if (!oVideoPlaybackController.isLive()) {
                oVideoPlaybackController.updateLastViewedPosition();
                PlaybackDataModelImpl.getInstance().resetPlaybackData();
            }
            const livePlayable: IPlayable = {
                channel: data,
                playbackContentType: PlaybackContentType.LIVE,
            };
            oVideoPlaybackController.startPlayback(livePlayable);
            Diagnostics.info(
                TAG,
                'User tuned to other channel by entering channel number. ',
                channelNumberEntered,
            );
            Log.i(
                TAG,
                'User tuned to other channel by entering channel number. ',
                channelNumberEntered,
            );
            QoEAppLogger.setWatchContentEndReason(
                QoEAppLogger.PlaybackEndReason
                    .USER_TUNED_TO_OTHER_CHANNEL_BY_ENTERING_CHANNEL_NUMBER,
            );
            QoEAppLogger.logChannelChanged(
                playerInstance.getPlayerStats(),
                playerInstance.getPlayerCurrentTime(),
                playerInstance.getSeekRange(),
            );
            QoEAppLogger.resetProfilePlayingTime();
            QoEAppLogger.stopMediaHealthTimer();
            VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.CHANNEL_CHANGE);
        })
        .catch((err) => {
            Diagnostics.error(TAG, 'tuneToEnteredChannelNumber {error: ', err, '}');
            Log.e(TAG, 'tuneToEnteredChannelNumber {error: ', err, '}');
        });
};

export const setEnteringChannelNumberEnabled =
    (enteringChannelNumberEnabled: boolean) => (dispatch: Function, getState: Function) => {
        dispatch(setEnteringChannelNumberEnabledAction(enteringChannelNumberEnabled));
        const { infoBanner, inputFactory } = getState();
        const {
            isStandardInfoActive,
            isFullInfoActive,
            isTrickplayMenuActive,
            channelNumberEntered,
        } = infoBanner;

        const { key } = inputFactory;

        if (
            channelNumberEntered.length === 0 &&
            key === (Keys.KEYBOARD_KEY_0 || Keys.SAMSUNG_REMOTE_KEY_0)
        ) {
            return;
        }
        if (
            key >= (Keys.KEYBOARD_KEY_0 || Keys.SAMSUNG_REMOTE_KEY_0) &&
            key <= (Keys.KEYBOARD_KEY_9 || Keys.SAMSUNG_REMOTE_KEY_9) &&
            channelNumberEntered.length < CHANNEL_NUMBER_MAX_LENGTH
        ) {
            const enteredChannelNumber = `${channelNumberEntered}${key}`;
            dispatch(setEnteredChannelNumberAction(enteredChannelNumber));
            dispatch(updateEnteringChannelnumberEnabledTimer());
            if (isStandardInfoActive) dispatch(updateStandardInfoBannerTimer());
            if (isTrickplayMenuActive) dispatch(updateTrickPlayMenuTimer());
            if (isFullInfoActive) dispatch(updateFullInfoBannerTimer());
        }
        if (!enteringChannelNumberEnabled) {
            dispatch(setEnteredChannelNumberAction(''));
        }
    };

export const setActionsStripHighlightIndex =
    (actionsStripHighlightIndex: number) => (dispatch: Function) => {
        dispatch(setActionsStripHighlightIndexAction(actionsStripHighlightIndex));
        Diagnostics.info(TAG, 'setActionsStripHighlightIndex');
        Log.i(TAG, 'setActionsStripHighlightIndex');
    };

export const setActionsStripHighlightBorderActive =
    (actionsStripHighlightBorderActive: boolean) => (dispatch: Function) => {
        dispatch(setActionsStripHighlightBorderActiveAction(actionsStripHighlightBorderActive));
        Diagnostics.info(TAG, 'setActionsStripHighlightBorderActive');
        Log.i(TAG, 'setActionsStripHighlightBorderActive', actionsStripHighlightBorderActive);
    };

export const setActionStripAvailableActionItems =
    (availableActionItems: Array<any>) => (dispatch: Function) => {
        dispatch(setAvailableActionItemsAction(availableActionItems));
    };

export const setActionItemsCount = (actionItemsCount: number) => (dispatch: Function) => {
    dispatch(setActionItemsCountAction(actionItemsCount));
    Diagnostics.info(TAG, 'setActionItemsCount');
    Log.i(TAG, 'setActionItemsCount');
};

export const showStandardInfo = () => (dispatch: Function, getState: Function) => {
    const { isOverlayDialogActive } = getState().overlayDialog;
    const { showOneLineGuide } = getState().oneLineGuide;

    if (!isOverlayDialogActive && !showOneLineGuide) {
        Diagnostics.debug(
            TAG,
            'showStandardInfo {screenTaskController: ',
            ScreenTaskControllerTypes.STANDARD_INFO,
            '}',
        );
        Log.d(
            TAG,
            'showStandardInfo {screenTaskController: ',
            ScreenTaskControllerTypes.STANDARD_INFO,
            '}',
        );
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.STANDARD_INFO));
        dispatch(setIsStandardInfoActive(true));
        dispatch(updateStandardInfoBannerTimer());
    }
};

export const hideStandardInfo = () => (dispatch: Function, getState: Function) => {
    Diagnostics.info(TAG, 'hideStandardInfo');
    Log.i(TAG, 'hideStandardInfo');
    const { infoBanner } = getState();
    const { standardInfoTimeoutId, enteringChannelNumberEnabled } = infoBanner;
    if (enteringChannelNumberEnabled) {
        dispatch(setIsStandardInfoActive(false));
        dispatch(setDefaultVideoPlayerTask());
        dispatch(setEnteringChannelNumberEnabled(false));
        dispatch(setEnteredChannelNumberAction(''));
    } else if (
        !VideoPlaybackController.getInstance().isTrickplayEnabled() ||
        !VideoPlaybackController.getInstance().isNetworkConnected()
    ) {
        dispatch(setIsStandardInfoActive(false));
        dispatch(setDefaultVideoPlayerTask());
    }
    if (standardInfoTimeoutId) clearTimeout(standardInfoTimeoutId);
};

export const setDefaultVideoPlayerTask = () => (dispatch: Function, getState: Function) => {
    const { isOverlayDialogActive } = getState().overlayDialog;
    const isFullScreenPlayer =
        VideoPlaybackController.getInstance().getPlayerViewMode() === PlayerViewMode.FullScreen;

    if (!isOverlayDialogActive && isFullScreenPlayer) {
        Log.d(
            TAG,
            'setDefaultVideoPlayerTask {screenTaskController: ',
            ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS,
            '}',
        );
        Diagnostics.debug(
            TAG,
            'setDefaultVideoPlayerTask {screenTaskController: ',
            ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS,
            '}',
        );
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS));
    }
};

export const updateStandardInfoBannerTimer = () => (dispatch: Function, getState: Function) => {
    const { infoBanner } = getState();
    const { standardInfoTimeoutId } = infoBanner;
    if (standardInfoTimeoutId) clearTimeout(standardInfoTimeoutId);
    const timer = setTimeout(() => {
        dispatch(hideStandardInfo());
    }, STANDARD_INFO_BANNER_DISPLAY_TIME);
    dispatch(updateStandardInfoTimeoutIdAction(timer));
    Diagnostics.info(TAG, 'updateStandardInfoBannerTimeout');
    Log.i(TAG, 'updateStandardInfoBannerTimeout');
};

const callActionListApi = () => {
    const playable = VideoPlaybackController.getInstance().getPlayable();
    if (playable) {
        switch (playable.playbackContentType) {
            case PlaybackContentType.LIVE:
                VideoPlaybackController.getInstance().prepareMetadataByType(
                    playable,
                    MetadataType.ACTION_LIST,
                );
                break;
            case PlaybackContentType.STARTOVER_BEGINNING:
            case PlaybackContentType.STARTOVER_RESUME:
            case PlaybackContentType.CATCHUP_BEGINNING:
            case PlaybackContentType.CATCHUP_RESUME:
            case PlaybackContentType.VOD:
                VideoPlaybackController.getInstance().prepareMetadataByType(
                    playable,
                    MetadataType.VOD_ACTION_LIST,
                );
                break;
            case PlaybackContentType.NDVR_LIVE:
            case PlaybackContentType.NDVR_RECORDED:
                VideoPlaybackController.getInstance().prepareMetadataByType(
                    playable,
                    MetadataType.RECORDING_ACTION_LIST,
                );
                break;
            default:
                break;
        }
    }
};

export const showFullInfo = () => (dispatch: Function, getState: Function) => {
    const { isOverlayDialogActive } = getState().overlayDialog;
    const { infoBanner } = getState();
    const { actionStripReference } = infoBanner;
    const { showOneLineGuide } = getState().oneLineGuide;

    if (!isOverlayDialogActive && !showOneLineGuide) {
        const oVideoPlaybackController = VideoPlaybackController.getInstance();
        const { airingOffers, offerPreview } = oVideoPlaybackController.getMetadata();
        const isExplicitAdultProgram =
            offerPreview?.contentPreview?.isAdult ??
            airingOffers?.[0]?.parentalControls.isAdult ??
            false;
        const adultRestriction =
            oVideoPlaybackController.getRestrictionType() === PlaybackRestrictionType.ADULT;
        const isPCPinOverlayRequired =
            isExplicitAdultProgram &&
            adultRestriction &&
            VideoPlaybackController.getInstance().isNetworkConnected();
        if (isPCPinOverlayRequired) {
            dispatch(showPCPinOverlayForVideoPlayer(showFullInfo));
            return;
        }
        Log.d(
            TAG,
            'showFullInfo {screenTaskController: ',
            ScreenTaskControllerTypes.FULL_INFO,
            '}',
        );
        dispatch(updateFullInfoBannerTimer());
        dispatch(setIsStandardInfoActive(true));
        dispatch(setActionsStripHighlightIndex(0));
        dispatch(setIsActionsStripActive(true));
        Diagnostics.debug(
            TAG,
            'showFullInfo {screenTaskController: ',
            ScreenTaskControllerTypes.FULL_INFO,
            '}',
        );

        dispatch(setScreenTaskController(ScreenTaskControllerTypes.FULL_INFO));
        dispatch(setIsFullInfoActive(true));
        callActionListApi();
        if (actionStripReference?.current)
            actionStripReference.current.style.marginLeft = `${DimensionScaler.getInstance().getScaledWidth(ACTION_STRIP_HIGHLIGHT_LEFT_OFFSET)}px`;
    }
};

export const hideFullInfo = () => (dispatch: Function, getState: Function) => {
    Diagnostics.info(TAG, 'hideFullInfo');
    Log.i(TAG, 'hideFullInfo');
    dispatch(hideStandardInfo());
    dispatch(setIsActionsStripActive(false));
    dispatch(setIsFullInfoActive(false));
    dispatch(setDefaultVideoPlayerTask());
    const { infoBanner } = getState();
    const { fullInfoTimeoutId } = infoBanner;
    if (fullInfoTimeoutId) clearTimeout(fullInfoTimeoutId);
};

export const updateFullInfoBannerTimer = () => (dispatch: Function, getState: Function) => {
    const { infoBanner } = getState();
    const { fullInfoTimeoutId, standardInfoTimeoutId } = infoBanner;
    if (fullInfoTimeoutId) clearTimeout(fullInfoTimeoutId);
    if (standardInfoTimeoutId) clearTimeout(standardInfoTimeoutId);

    const timer = setTimeout(() => {
        dispatch(hideFullInfo());
    }, FULL_INFO_BANNER_DISPLAY_TIME);
    dispatch(updateFullInfoTimeoutIdAction(timer));
    Diagnostics.info(TAG, 'updateFullInfoBannerTimeout');
    Log.i(TAG, 'updateFullInfoBannerTimeout');
};

export const clearLongKeyPressInterval = () => (dispatch: Function, getState: Function) => {
    const { trickPlayBarLongKeyPressIntervalId } = getState().infoBanner;
    if (trickPlayBarLongKeyPressIntervalId) {
        clearInterval(trickPlayBarLongKeyPressIntervalId);
        dispatch(updateTrickPlayBarLongKeyPressIntervalIdAction(null));
        Diagnostics.info(TAG, ' clearLongKeyPressInterval');
        Log.i(TAG, ' clearLongKeyPressInterval');
    }
};

export const showTrickPlayMenuOnStartUp = () => (dispatch: Function, getState: Function) => {
    const { isStandardInfoActive, isFullInfoActive, isActionsStripActive } = getState().infoBanner;
    const { showOneLineGuide } = getState().oneLineGuide;

    if (!(isStandardInfoActive || isFullInfoActive || isActionsStripActive) && !showOneLineGuide) {
        dispatch(showTrickPlayMenu());
        Diagnostics.info(TAG, 'showTrickPlayMenuOnStartUp');
        Log.i(TAG, 'showTrickPlayMenuOnStartUp');
    }
};

export const showTrickPlayMenu = () => (dispatch: Function, getState: Function) => {
    const { showOneLineGuide } = getState().oneLineGuide;
    const { isOverlayDialogActive } = getState().overlayDialog;

    if (!isOverlayDialogActive && !showOneLineGuide) {
        Diagnostics.debug(
            TAG,
            'showTrickPlayMenu {screenTaskController: ',
            ScreenTaskControllerTypes.TRICKPLAY_MENU,
            '}',
        );
        dispatch(setCurrentActiveIndexAction(MediaButtonTypes.PLAY_PAUSE_BUTTON_INDEX));
        dispatch(setMediaButtonMouseOverIndex(-1));
        dispatch(setIsFullInfoActive(false));
        dispatch(setIsActionsStripActive(false));
        dispatch(setIsStandardInfoActive(true));
        dispatch(setIsTrickplayMenuActive(true));
        Log.d(
            TAG,
            'showTrickPlayMenu {screenTaskController: ',
            ScreenTaskControllerTypes.TRICKPLAY_MENU,
            '}',
        );
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.TRICKPLAY_MENU));
        dispatch(updateTrickPlayMenuTimer());
        dispatch(updateStandardInfoBannerTimer());
        callActionListApi();
        KeyManager.getInstance().stopKeyIgnoring();
    }
};

export const hideTrickPlayMenu = () => (dispatch: Function, getState: Function) => {
    Diagnostics.info(TAG, 'hideTrickplayMenu');
    Log.i(TAG, 'hideTrickplayMenu');
    const { infoBanner } = getState();
    const { trickPlayBarTimeoutId } = infoBanner;
    if (
        !VideoPlaybackController.getInstance().isTrickplayEnabled() ||
        !VideoPlaybackController.getInstance().isNetworkConnected()
    ) {
        dispatch(hideStandardInfo());
        dispatch(setIsTrickplayMenuActive(false));
        dispatch(setDefaultVideoPlayerTask());
    }
    if (trickPlayBarTimeoutId) clearTimeout(trickPlayBarTimeoutId);
    dispatch(hideNotSupportedText());
    dispatch(clearLongKeyPressInterval());
};

export const updateTrickPlayMenuTimer = () => (dispatch: Function, getState: Function) => {
    const { infoBanner } = getState();
    const { trickPlayBarTimeoutId } = infoBanner;
    if (trickPlayBarTimeoutId) clearTimeout(trickPlayBarTimeoutId);
    const timer = setTimeout(() => {
        dispatch(hideTrickPlayMenu());
    }, TRICK_PLAY_DISPLAY_TIME);
    dispatch(updateTrickPlayBarTimeoutIdAction(timer));
    Diagnostics.info(TAG, 'updateTrickplayTimeout');
    Log.i(TAG, 'updateTrickplayTimeout');
};

export const hideInfoBanners = () => (dispatch: Function, getState: Function) => {
    const { isActionsStripActive, isStandardInfoActive, isFullInfoActive, isTrickplayMenuActive } =
        getState().infoBanner;
    if (isActionsStripActive || isFullInfoActive) dispatch(hideFullInfo());
    if (isStandardInfoActive) dispatch(hideStandardInfo());
    if (isTrickplayMenuActive) dispatch(hideTrickPlayMenu());
    Diagnostics.info(TAG, 'Hide if any infobanner is active.');
    Log.i(TAG, 'Hide if any infobanner is active.');
};

export default infoBannerReducers;
